/**
 * PRODUCT GRID — <column-switcher> and <load-more>.
 * docs/09 §6, §9 · docs/17 §7, §8 · docs/39 §6.
 *
 * IIFE-wrapped. docs/54 §5.6: the collection grid and the search grid both
 * emit this tag, and a page that renders two grid-bearing sections fetches
 * the file once and EXECUTES it twice. A top-level `class` re-declared that
 * way throws at parse time, before any customElements.get guard below it can
 * run. That cost section-scrolling-promotion.js a whole page at Step 5.
 */
(function () {
  if (customElements.get('column-switcher')) return;

  /* ---------------------------------------------------------------------- *
   * <column-switcher> — docs/09 §6, docs/17 §7
   *
   * Density is a `cols-N` class on the grid, matching the reference's
   * `.m-cols-N`. See the note in component-product-grid.css §5 for why it
   * cannot be a custom property: `repeat()` rejects `min()`, so the per-band
   * cap has to live in selectors.
   *
   * The choice persists in sessionStorage rather than the URL. It is a
   * viewing preference, not a query — putting it in the URL would make every
   * shared collection link carry someone else's density, and would collide
   * with the filter params facets.js owns.
   * ---------------------------------------------------------------------- */
  /**
   * DENSITY IS PER BAND — 2026-08-13, docs/54 §1.-43.
   *
   * This was one key for the whole session, so a density chosen on a desktop
   * grid was replayed on a phone. Reported by the merchant as "on mobile the
   * products should appear in two columns": picking LIST view (`cols-1`) on
   * desktop — a layout choice, docs/09 §6.2 — turned the 390 grid into a
   * single 360px card per screen. Reproduced exactly by storing `1` and
   * reloading at 390.
   *
   * Three columns leaked the same way and worse: `.column-switcher__button--wide-only`
   * hides 3, 4 and 5 below 768 (component-product.css), but `readStored`
   * validated against buttons that EXIST rather than buttons this band
   * OFFERS, so a stored `5` would have applied five columns to a phone.
   *
   * A per-band key fixes the cause rather than clamping the symptom: each band
   * keeps its own preference, and neither can speak for the other. The
   * breakpoint is the one the switcher's own CSS uses.
   */
  const STORE_PREFIX = 'dramour:grid-columns';
  const band = () => (window.matchMedia('(min-width: 768px)').matches ? 'wide' : 'narrow');
  const storeKey = () => `${STORE_PREFIX}:${band()}`;
  const COLS = [1, 2, 3, 4, 5];

  class ColumnSwitcher extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.buttons = [...this.querySelectorAll('[data-columns]')];
      this.addEventListener('click', this.onClick.bind(this));

      const stored = this.readStored();
      if (stored && stored !== this.current) this.apply(stored, { announce: false });

      this.watchGrid();
    }

    /**
     * facets.js replaces #ProductGridContainer's innerHTML on every filter
     * change, so the grid <ul> that carries `cols-N` is destroyed and the
     * server's default class comes back. The switcher itself lives outside
     * that container — deliberately, so a filter cannot destroy the control
     * mid-interaction — which means nothing re-applies the choice unless
     * something watches.
     *
     * Dawn dispatches no event for this, so a MutationObserver on the
     * container is the only honest hook. Guarded on the class already being
     * right, or the re-apply would itself be a mutation and loop.
     */
    watchGrid() {
      const container = this.grid?.closest('#ProductGridContainer');
      if (!container || !window.MutationObserver) return;

      new MutationObserver(() => {
        // `chosen` gates this: until the visitor picks a density the grid is
        // meant to carry NO cols class, so the per-band defaults in
        // component-product-grid.css §5 apply. Re-applying unconditionally
        // would stamp the desktop count onto every band on the first filter.
        if (!this.chosen) return;
        const grid = this.grid;
        if (!grid || grid.classList.contains(`cols-${this.current}`)) return;
        this.apply(this.current, { announce: false });
      }).observe(container, { childList: true, subtree: true });
    }

    get grid() {
      // Re-queried every time: facets.js replaces #ProductGridContainer's
      // innerHTML wholesale on every filter change, so a reference captured
      // at connect time points at a detached node afterwards.
      return document.getElementById(this.dataset.grid);
    }

    get current() {
      const pressed = this.buttons.find((b) => b.getAttribute('aria-pressed') === 'true');
      return pressed ? Number(pressed.dataset.columns) : Number(this.dataset.initial || 4);
    }

    readStored() {
      try {
        const n = Number(sessionStorage.getItem(storeKey()));
        // Only honour a density this band actually OFFERS. `offsetParent` is
        // null for a `display: none` button, which is how the CSS hides 3, 4
        // and 5 below 768 — checking the button merely EXISTS would let a
        // five-column desktop choice land on a phone.
        return this.buttons.some((b) => Number(b.dataset.columns) === n && b.offsetParent !== null) ? n : null;
      } catch {
        return null;
      }
    }

    onClick(event) {
      const button = event.target.closest('[data-columns]');
      if (!button || !this.contains(button)) return;
      this.apply(Number(button.dataset.columns));
    }

    apply(columns, { announce = true } = {}) {
      this.chosen = true;
      this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.columns) === columns)));

      const grid = this.grid;
      if (grid) {
        // docs/09 §6.2: list view is a LAYOUT change, not a density change.
        // `cols-1` is also the card's OWN hook — component-product-card.css
        // §6 built the whole rail relocation behind it at Step 5. The
        // switcher sets the class; the card decides what it means.
        COLS.forEach((n) => grid.classList.toggle(`cols-${n}`, n === columns));
      }

      try {
        sessionStorage.setItem(storeKey(), String(columns));
      } catch {
        /* private mode — the preference just does not persist */
      }

      if (announce) {
        const label = this.buttons.find((b) => Number(b.dataset.columns) === columns)?.getAttribute('aria-label');
        if (label) announce_(label);
      }
    }
  }

  /* ---------------------------------------------------------------------- *
   * <load-more> — docs/39 §6, docs/09 §9
   *
   * Section Rendering API, per docs/47 §18.5: fetch the next page as the
   * SAME section and lift the rendered <li>s out of it. Never build card
   * markup in JS — six sections render that card and a JS copy would be a
   * seventh that drifts.
   * ---------------------------------------------------------------------- */
  class LoadMore extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';
      this.button = this.querySelector('[data-load-more]');
      this.button?.addEventListener('click', this.onClick.bind(this));
    }

    get grid() {
      return document.getElementById(this.dataset.grid);
    }

    async onClick() {
      const next = this.dataset.nextUrl;
      if (!next || this.loading) return;
      this.loading = true;

      // docs/39 §10's spinner contract: the button's children go
      // visibility:hidden and pointer-events are off, so a second press
      // cannot fire while the fetch is in flight.
      this.button.classList.add('loading');
      this.button.setAttribute('aria-busy', 'true');

      const grid = this.grid;
      // Dawn's own hook — facets.js reads the section id from the same
      // attribute on the same element (getSections()).
      const sectionId = grid?.dataset.id;

      try {
        const url = new URL(next, window.location.origin);
        if (sectionId) url.searchParams.set('section_id', sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`Load More: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        const newGrid = doc.getElementById(this.dataset.grid);
        const items = newGrid ? [...newGrid.children] : [];

        // `importNode` returns a COPY. Keeping a reference to the parsed-doc
        // node instead would make the focus move below a silent no-op, since
        // `.focus()` on an element outside the live document does nothing —
        // which is exactly what the first measurement of this showed.
        let firstAppended = null;
        items.forEach((li) => {
          const node = grid.appendChild(document.importNode(li, true));
          if (!firstAppended) firstAppended = node;
        });

        // The count and the next url both live in the fetched pagination
        // block. Reading them from the response rather than computing them
        // keeps one source: Liquid already capped `shown` at `items`.
        const newPagination = doc.querySelector('.pagination--load-more');
        const count = this.closest('.pagination')?.querySelector('.pagination__count');
        const newCount = newPagination?.querySelector('.pagination__count');
        if (count && newCount) {
          count.textContent = newCount.textContent;
          count.dataset.shown = newCount.dataset.shown || '';
          count.dataset.total = newCount.dataset.total || '';
        }

        const newNext = newPagination?.querySelector('load-more')?.dataset.nextUrl;
        if (newNext) {
          this.dataset.nextUrl = newNext;
        } else {
          // Exhausted — docs/17 §8 infers the button hides. Focus would be
          // destroyed with it, so it moves to the first new card first.
          this.remove();
        }

        // Keyboard continuity: without this, pressing Load More sends focus
        // to <body> the moment the button is removed, and a keyboard user
        // restarts the page.
        const target = firstAppended?.querySelector('a, button') || firstAppended;
        if (target) {
          if (!target.hasAttribute('tabindex') && target.tagName === 'LI') target.tabIndex = -1;
          target.focus({ preventScroll: true });
        }
      } catch (error) {
        console.error(error);
        // Leave the button usable — a failed fetch should be retryable.
      } finally {
        this.loading = false;
        this.button?.classList.remove('loading');
        this.button?.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * One shared polite live region for the column switcher. The pagination
   * count is its own `aria-live` element (docs/39 §6) because it is visible
   * text that changes; the density change has no visible text of its own.
   */
  function announce_(message) {
    let region = document.getElementById('GridLiveRegion');
    if (!region) {
      region = document.createElement('p');
      region.id = 'GridLiveRegion';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    region.textContent = message;
  }

  customElements.define('column-switcher', ColumnSwitcher);
  customElements.define('load-more', LoadMore);
})();
